import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Badge } from "@/components/ui/badge"
import { Calendar, User, Info } from "lucide-react"

interface CodeDetailsDialogProps {
  isOpen: boolean
  onClose: () => void
  code: {
    code: string
    key_name: string
    key_id: string
    client_name: string
    client_email?: string | null
    client_phone?: string | null
    status: string
    created_at: string
    sent_at?: string | null
    cancelled_at?: string | null
    scheduled_for?: string | null
    used_at?: string | null
  } | null
}

export function CodeDetailsDialog({ isOpen, onClose, code }: CodeDetailsDialogProps) {
  if (!code) return null

  const formatDate = (date: string | null | undefined) => {
    if (!date) return "-"
    return new Date(date).toLocaleString('fr-FR')
  }

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'pending':
        return 'default'
      case 'sent':
        return 'secondary'
      case 'cancelled':
        return 'destructive'
      case 'used':
        return 'success'
      default:
        return 'default'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'En attente'
      case 'sent':
        return 'Envoyé'
      case 'cancelled':
        return 'Annulé'
      case 'used':
        return 'Utilisé'
      default:
        return status
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Détails du code {code.code}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Informations de la clé */}
          <div className="space-y-2">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Info className="h-5 w-5" />
              Informations de la clé
            </h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="text-muted-foreground">Nom de la clé</div>
              <div>{code.key_name}</div>
              <div className="text-muted-foreground">ID de la clé</div>
              <div>{code.key_id}</div>
              <div className="text-muted-foreground">Statut</div>
              <div>
                <Badge variant={getStatusBadgeVariant(code.status)}>
                  {getStatusLabel(code.status)}
                </Badge>
              </div>
            </div>
          </div>

          {/* Informations du client */}
          <div className="space-y-2">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <User className="h-5 w-5" />
              Informations du client
            </h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="text-muted-foreground">Nom</div>
              <div>{code.client_name}</div>
              <div className="text-muted-foreground">Email</div>
              <div>{code.client_email || "-"}</div>
              <div className="text-muted-foreground">Téléphone</div>
              <div>{code.client_phone || "-"}</div>
            </div>
          </div>

          {/* Timeline */}
          <div className="space-y-2">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Timeline
            </h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="text-muted-foreground">Créé le</div>
              <div>{formatDate(code.created_at)}</div>
              {code.scheduled_for && (
                <>
                  <div className="text-muted-foreground">Programmé pour le</div>
                  <div>{formatDate(code.scheduled_for)}</div>
                </>
              )}
              {code.sent_at && (
                <>
                  <div className="text-muted-foreground">Envoyé le</div>
                  <div>{formatDate(code.sent_at)}</div>
                </>
              )}
              {code.cancelled_at && (
                <>
                  <div className="text-muted-foreground">Annulé le</div>
                  <div>{formatDate(code.cancelled_at)}</div>
                </>
              )}
              {code.used_at && (
                <>
                  <div className="text-muted-foreground">Utilisé le</div>
                  <div>{formatDate(code.used_at)}</div>
                </>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}